<script lang="ts" setup>
import { usePayStore } from '@/modules/pay/store/payStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useRouter } from 'vue-router'
import { onUnmounted, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useApiStore } from '@/modules/api/store/apiStore'
import { EventLogger } from '@/utils/EventLogger'
import { ERouteNames } from '@/router/ERouteNames'

const { t } = useI18n()
const payStore = usePayStore()
const feedbackStore = useFeedbackStore()
const router = useRouter()
const apiStore = useApiStore()

onMounted(async () => {
  await nextTick()

  const paymentForm = document.getElementById('payment-form')
  const userStore = useUserStore()

  // Виджет грузится в фоне при старте приложения (preloadExternalScripts)
  if (!window.YooMoneyCheckoutWidget) {
    EventLogger.error('PaymentOverlay', [
      'YooKassa widget unavailable at payment open',
    ])
    feedbackStore.showToast({
      type: 'error',
      message: t('payment.widgetUnavailable'),
      time: 15000,
    })
    payStore.$reset()
    payStore.isTariffActiveSuccessful = false
    await router.push({ name: ERouteNames.HOME })
    return
  }

  if (paymentForm && window.YooMoneyCheckoutWidget) {
    try {
      const checkout = new window.YooMoneyCheckoutWidget({
        confirmation_token: payStore.confirmationToken,
        customization: {
          colors: {
            background: '#1E2532',
            control_primary: '#5237EA',
            control_primary_content: '#F7F7F7',
            control_secondary: '#2D3749',
            border: '#2D3749',
            text: '#F7F7F7',
          },
        },
        error_callback: (error) => {
          console.error('Ошибка при инициализации виджета:', error)
          EventLogger.error('PaymentOverlay_error_callback', [
            `Ошибка: ${error}`,
          ])

          feedbackStore.showToast({
            type: 'error',
            message: String(error),
            time: 15000,
          })

          payStore.$reset()
          payStore.isTariffActiveSuccessful = false
          checkout.destroy()
          router.push({name: ERouteNames.HOME})
        },
      })

      // Обработка успешной оплаты
      checkout.on('success', async () => {

        await payStore.streamPaymentStatus({ paymentId: payStore.paymentId })
        if (payStore.isTariffActiveSuccessful) {
          const paidUserDataSuccessfullyUpdated = await userStore.getUserData()
          if (paidUserDataSuccessfullyUpdated) {
            await apiStore.getUserToServers()
            feedbackStore.showToast({
              type: 'success',
              message: t('payment.success'),
            })
            checkout.destroy()
            await router.push({name: ERouteNames.HOME})
            return
          }
        }
        else {
          payStore.$reset()
          payStore.isTariffActiveSuccessful = false
          checkout.destroy()
          EventLogger.error('checkout.on success', [
            'Условие paidUserDataSuccessfullyUpdated после успешной оплаты не true (юзер не получен)',
            'Компонент: PaymentOverlay.vue'
          ])
          await router.push({name: ERouteNames.HOME})
          
          const errMsg = payStore.paymentError?.message || payStore.paymentError?.systemMessage
          if (errMsg) {
            feedbackStore.showToast({
              type: 'error',
              message: errMsg,
              time: 15000,
            })
          }
        }
      })

      // Обработка неудачной оплаты
      checkout.on('fail', () => {
        EventLogger.error('checkout.on fail', [
          'Попали в checkout.on fail без явной ошибки',
          'Компонент: PaymentOverlay.vue'
        ])
        feedbackStore.showToast({
          type: 'error',
          message: t('payment.failed'),
          time: 15000,
        })
        payStore.$reset()
        payStore.isTariffActiveSuccessful = false
        checkout.destroy()
        router.push({name: ERouteNames.HOME})
      })

      checkout.render('payment-form')
    } catch (error) {
      EventLogger.error('checkout.on fail', [
        `Попали в catch error блока checkout.on fail ошибка - ${error}`,
        'Компонент: PaymentOverlay.vue'
      ])
      feedbackStore.showToast({
        type: 'error',
        message: error instanceof Error ? error.message : String(error),
        time: 15000,
      })

      payStore.$reset()
      payStore.isTariffActiveSuccessful = false
      router.push({name: ERouteNames.HOME})
    }
  }
})

onUnmounted(() => {
  payStore.$reset()
  payStore.isTariffActiveSuccessful = false
})
</script>

<template>
  <div class="payment_overlay">
    <div id="payment-form"></div>
  </div>
</template>

<style scoped>
.payment_overlay {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  background-color: transparent !important;
}

#payment-form {
  color-scheme: only light !important;
}
</style>
